import React, { useEffect, useState, useRef } from "react";
import api from "../api/api";

interface Props {
  minutes: number;        // total minutes after unit conversion
  comment: string;        // current comment value
}

/**
 * Inline hint shown below the comment field.
 * Explains when a comment is required or recommended, and shows a live character counter.
 */
export default function CommentPolicyHint({ minutes, comment }: Props) {
  const [verdict, setVerdict] = useState<{ required: boolean; recommended: boolean; minLength: number } | null>(null);
  const timer = useRef<any>(null);

  useEffect(() => {
    if (!minutes || minutes < 1) { setVerdict(null); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/comment-policy/check?minutes=${minutes}`);
        setVerdict(r.data || null);
      } catch { setVerdict(null); }
    }, 250);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [minutes]);

  if (!verdict) return null;
  if (!verdict.required && !verdict.recommended) return null;

  const len = (comment || "").trim().length;
  const min = verdict.minLength || 0;
  const isOk = verdict.required ? len >= min : true;

  if (verdict.required) {
    return (
      <div className={`mt-1 text-xs flex items-center gap-2 ${isOk ? "text-green-600" : "text-red-600"}`}>
        <span>{isOk ? "✓" : "⚠️"}</span>
        <span>
          <strong>Comment required</strong> for {minutes} min · {len}/{min} characters
          {!isOk && ` · add ${min - len} more`}
        </span>
      </div>
    );
  }

  // recommended
  return (
    <div className="mt-1 text-xs flex items-center gap-2 text-amber-600">
      <span>💡</span>
      <span>A short comment is recommended for {minutes} min (optional)</span>
    </div>
  );
}
